/**
 * @file DialogRequestForTraining Component
 * @description This component provides a dialog interface for changing user passwords.
 * It includes form validation, password strength indicators, and API integration.
 */
import InputFieldsController from '../form/controller/input-fields.controller';

import DatePickerController from '@components/form/controller/date-picker.controller';
import { yupResolver } from '@hookform/resolvers/yup';
import useToast from '@hooks/useToast';
import { SchoolOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { dialogRequestForTrainingSchema } from '@utils/schema';
import moment, { Moment } from 'moment';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { getColor } from '../../assets/theme';
import DialogWrapper from './dialog-wrapper';
import { useEmailRequestTrainingMutation } from '@api/property.api.rtk';
import { useAppSelector } from '@store/index';

/**
 * @interface DialogRequestForTrainingProps
 * @description Props for the DialogRequestForTraining component
 */
interface DialogRequestForTrainingProps {
  open: boolean;
  onClose: () => void;
}

/**
 * @interface FormValues
 * @description Structure for the form values
 */
interface FormValues {
  remarks?: string;
  preferredDate: Moment;
}

export interface IEmailRequestTraining {
  properties: {
    property: {
      propertyID: string;
      requestData: {
        preferredDate: string;
        remarks: string;
      }[];
    };
  }[];
}

/**
 * DialogRequestForTraining Component
 * @param {DialogRequestForTrainingProps} props - Component props
 * @returns {JSX.Element} Rendered component
 */
const DialogRequestForTraining = ({
  open,
  onClose,
}: DialogRequestForTrainingProps) => {
  const { primary, white } = getColor();

  const { toast } = useToast();

  const [email, { isLoading }] = useEmailRequestTrainingMutation();

  const methods = useForm<FormValues>({
    defaultValues: {
      remarks: '',
      preferredDate: moment().tz('Asia/Manila'),
    },
    resolver: yupResolver<FormValues>(dialogRequestForTrainingSchema),
    mode: 'onSubmit',
  });

  const property = useAppSelector(
    (state) => state.properties.selectedProperty.value,
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = methods;

  /**
   * Handle form submission
   * @param {FormValues} data - Form data
   */
  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const payload: IEmailRequestTraining = {
      properties: [
        {
          property: {
            propertyID: property,
            requestData: [
              {
                preferredDate: data.preferredDate.format('YYYY-MM-DD'),
                remarks: data.remarks || '',
              },
            ],
          },
        },
      ],
    };
    await email(payload)
      .unwrap()
      .then(() => {
        toast({
          message:
            "We've received your request! Our deployment team will contact you soon to confirm the trainer's availability.",
          severity: 'success',
        });
        onClose();
      })
      .catch((err) =>
        toast({
          severity: 'error',
          message: err,
        }),
      );
  };

  return (
    <DialogWrapper
      open={open}
      onClose={() => {
        !isLoading && onClose();
      }}
    >
      <Box sx={{ width: { xs: '100%', md: '450px' } }}>
        <Stack
          sx={{
            bgcolor: white,
            borderRadius: '4px',
            p: '24px',
            justifyContent: 'flex-start',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '-36px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 999,
            }}
          >
            <Stack
              sx={{
                width: '79px',
                height: '79px',
                bgcolor: '#FFFF',
                borderRadius: '100%',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <SchoolOutlined
                sx={{
                  width: '59px',
                  height: '59px',
                  bgcolor: '#F7F7FB',
                  p: '12px',
                  borderRadius: '100%',
                  color: primary,
                }}
              />
            </Stack>
          </Box>
          <DialogTitle sx={{ p: '8px 0', pt: 2, fontWeight: 600 }}>
            Request Training
          </DialogTitle>
          <FormProvider {...methods}>
            <Stack component='form' onSubmit={handleSubmit(onSubmit)}>
              <DialogContent sx={{ p: '16px 0!important' }}>
                <Stack gap={2}>
                  <DatePickerController
                    control={control}
                    name='preferredDate'
                    label='Preferred Date *'
                    disablePast={true}
                    errors={errors}
                  />
                  <InputFieldsController
                    control={control}
                    errors={errors}
                    label='Remarks'
                    name='remarks'
                    wrapperStyle={{
                      marginBottom: 0,
                    }}
                    multiline
                    rows={5}
                    inputStyle={{
                      '.MuiInputBase-root': { height: '' },
                    }}
                    inputProps={{ maxLength: 999 }}
                    borderErrorOnly
                    showTextLength
                    placeholder=' '
                  />
                </Stack>
              </DialogContent>
              <DialogActions sx={{ p: '0 0 8px 0' }}>
                <Stack direction='row' gap='8px'>
                  <Button
                    variant='outlined'
                    onClick={onClose}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    variant='contained'
                    type='submit'
                    loading={isLoading}
                  >
                    Request
                  </LoadingButton>
                </Stack>
              </DialogActions>
            </Stack>
          </FormProvider>
        </Stack>
      </Box>
    </DialogWrapper>
  );
};

export default DialogRequestForTraining;
